import { SUPPORTED_CURRENCY_CODES } from '@shared/currency.ts';
import { getDateLocale } from '../i18n/locale.ts';

/**
 * The currency picker's options.
 *
 * The codes come from the shared table — the same one the API validates
 * against and the payment adapters convert amounts with — so the list can
 * never offer a currency the app would then refuse to charge in. Only the
 * names are localized, by the runtime rather than by a translated table of
 * our own.
 */

/** "USD — US Dollar" where the runtime knows the name, plain "USD" where not. */
function currencyLabel(code: string, locale: string): string {
  try {
    const name = new Intl.DisplayNames([locale], { type: 'currency' }).of(code);
    return name && name !== code ? `${code} — ${name}` : code;
  } catch {
    return code;
  }
}

/**
 * Every selectable currency, with `current` guaranteed present.
 *
 * Same reason as the timezone list: a company sitting on a code this build no
 * longer offers must still see its own value selected, or opening Settings and
 * pressing Save would silently redenominate them.
 */
export function currencyOptions(
  current: string,
  locale: string = getDateLocale(),
): Array<{ value: string; label: string }> {
  const codes = SUPPORTED_CURRENCY_CODES.includes(current)
    ? SUPPORTED_CURRENCY_CODES
    : [current, ...SUPPORTED_CURRENCY_CODES];
  return codes.map((code) => ({ value: code, label: currencyLabel(code, locale) }));
}
